import React, { useState, useEffect } from 'react';
import { supabaseAdmin as supabase } from '../../lib/supabase.js';
import './TimetableSettings.css';

const TimetableSettings = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  // Default configuration based on TIMETABLE_LOGIC.md
  const defaultConfig = {
    academic_year: "2024-2025",
    total_periods: 15,
    days_of_week: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
    time_slots: [
      { period_sequence: 1, time_range: "8:00-8:10", period_node: "班主任節", period_number: 0 },
      { period_sequence: 2, time_range: "8:10-8:45", period_node: "Period 1", period_number: 1 },
      { period_sequence: 3, time_range: "8:45-9:20", period_node: "Period 2", period_number: 2 },
      { period_sequence: 4, time_range: "9:20-9:55", period_node: "Period 3", period_number: 3 },
      { period_sequence: 5, time_range: "9:55-10:15", period_node: "Recess", period_number: 0 },
      { period_sequence: 6, time_range: "10:15-10:50", period_node: "Period 4", period_number: 4 },
      { period_sequence: 7, time_range: "10:50-11:25", period_node: "Period 5", period_number: 5 },
      { period_sequence: 8, time_range: "11:25-11:30", period_node: "Recess", period_number: 0 },
      { period_sequence: 9, time_range: "11:30-12:05", period_node: "Period 6", period_number: 6 },
      { period_sequence: 10, time_range: "12:05-12:40", period_node: "Period 7", period_number: 7 },
      { period_sequence: 11, time_range: "12:40-1:50", period_node: "Lunch", period_number: 0 },
      { period_sequence: 12, time_range: "1:50-2:25", period_node: "Period 8", period_number: 8 },
      { period_sequence: 13, time_range: "2:25-3:00", period_node: "Period 9", period_number: 9 },
      { period_sequence: 14, time_range: "3:00-3:35", period_node: "Period 10", period_number: 10 },
      { period_sequence: 15, time_range: "3:35-3:50", period_node: "班主任節", period_number: 0 }
    ],
    day_specific_overrides: {
      Friday: {
        period_14: {
          period_node: "班主任節",
          period_number: 0,
          note: "Friday special: Period 10 becomes 班主任節"
        }
      }
    }
  };

  useEffect(() => {
    loadConfiguration();
  }, []);

  const loadConfiguration = async () => {
    try {
      setLoading(true);
      
      // Try to load from database first
      const { data, error } = await supabase
        .from('timetable_configurations')
        .select('*')
        .eq('academic_year', defaultConfig.academic_year)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error loading configuration:', error);
        setConfig(defaultConfig);
      } else if (data) {
        setConfig(data.config);
      } else {
        setConfig(defaultConfig);
      }
    } catch (error) {
      console.error('Error loading configuration:', error);
      setConfig(defaultConfig);
    } finally {
      setLoading(false);
    }
  };

  const saveConfiguration = async () => {
    try {
      setSaving(true);
      
      const { error } = await supabase
        .from('timetable_configurations')
        .upsert({
          academic_year: config.academic_year,
          config: config,
          updated_at: new Date().toISOString()
        });

      if (error) {
        throw error;
      }

      setMessage('Configuration saved successfully!');
      setMessageType('success');
    } catch (error) {
      console.error('Error saving configuration:', error);
      setMessage('Error saving configuration: ' + error.message);
      setMessageType('error');
    } finally {
      setSaving(false);
    }
  };

  const updateTimeSlot = (index, field, value) => {
    const newConfig = { ...config };
    newConfig.time_slots[index][field] = value;
    setConfig(newConfig);
  };

  const updateDayOverride = (day, period, field, value) => {
    const newConfig = { ...config };
    if (!newConfig.day_specific_overrides[day]) {
      newConfig.day_specific_overrides[day] = {};
    }
    if (!newConfig.day_specific_overrides[day][period]) {
      newConfig.day_specific_overrides[day][period] = {};
    }
    newConfig.day_specific_overrides[day][period][field] = value;
    setConfig(newConfig);
  };

  const resetToDefault = () => {
    setConfig(defaultConfig);
    setMessage('Configuration reset to default');
    setMessageType('info');
  };

  const showMessage = (text, type) => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => {
      setMessage('');
      setMessageType('');
    }, 3000);
  };

  if (loading) {
    return (
      <div className="timetable-settings">
        <div className="loading">Loading configuration...</div>
      </div>
    );
  }

  return (
    <div className="timetable-settings">
      <div className="settings-header">
        <h2>📅 Timetable Configuration</h2>
        <p>Configure global timetable settings for academic year {config.academic_year}</p>
      </div>

      {message && (
        <div className={`message ${messageType}`}>
          {message}
        </div>
      )}

      <div className="settings-content">
        {/* Academic Year Configuration */}
        <div className="config-section">
          <h3>Academic Year</h3>
          <div className="form-group">
            <label>Academic Year:</label>
            <input
              type="text"
              value={config.academic_year}
              onChange={(e) => setConfig({ ...config, academic_year: e.target.value })}
              className="form-input"
            />
          </div>
        </div>

        {/* Time Slots Configuration */}
        <div className="config-section">
          <h3>Time Slots Configuration</h3>
          <div className="time-slots-table">
            <table>
              <thead>
                <tr>
                  <th>Period Sequence</th>
                  <th>Time Range</th>
                  <th>Period Node</th>
                  <th>Period Number</th>
                </tr>
              </thead>
              <tbody>
                {config.time_slots.map((slot, index) => (
                  <tr key={slot.period_sequence}>
                    <td>{slot.period_sequence}</td>
                    <td>
                      <input
                        type="text"
                        value={slot.time_range}
                        onChange={(e) => updateTimeSlot(index, 'time_range', e.target.value)}
                        className="form-input small"
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        value={slot.period_node}
                        onChange={(e) => updateTimeSlot(index, 'period_node', e.target.value)}
                        className="form-input small"
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        value={slot.period_number}
                        onChange={(e) => updateTimeSlot(index, 'period_number', parseInt(e.target.value))}
                        className="form-input small"
                        min="0"
                        max="10"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Day-Specific Overrides */}
        <div className="config-section">
          <h3>Day-Specific Overrides</h3>
          
          {/* Friday Special Case */}
          <div className="day-override">
            <h4>Friday Special Case</h4>
            <div className="override-controls">
              <label>
                <input
                  type="checkbox"
                  checked={config.day_specific_overrides.Friday?.period_14?.period_node === "班主任節"}
                  onChange={(e) => {
                    if (e.target.checked) {
                      updateDayOverride('Friday', 'period_14', 'period_node', '班主任節');
                      updateDayOverride('Friday', 'period_14', 'period_number', 0);
                    } else {
                      updateDayOverride('Friday', 'period_14', 'period_node', 'Period 10');
                      updateDayOverride('Friday', 'period_14', 'period_number', 10);
                    }
                  }}
                />
                Enable Friday special case (Period 10 = 班主任節)
              </label>
            </div>
            
            {config.day_specific_overrides.Friday?.period_14 && (
              <div className="override-details">
                <div className="form-group">
                  <label>Period Node:</label>
                  <input
                    type="text"
                    value={config.day_specific_overrides.Friday.period_14.period_node}
                    onChange={(e) => updateDayOverride('Friday', 'period_14', 'period_node', e.target.value)}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label>Period Number:</label>
                  <input
                    type="number"
                    value={config.day_specific_overrides.Friday.period_14.period_number}
                    onChange={(e) => updateDayOverride('Friday', 'period_14', 'period_number', parseInt(e.target.value))}
                    className="form-input"
                    min="0"
                    max="10"
                  />
                </div>
                <div className="form-group">
                  <label>Note:</label>
                  <input
                    type="text"
                    value={config.day_specific_overrides.Friday.period_14.note}
                    onChange={(e) => updateDayOverride('Friday', 'period_14', 'note', e.target.value)}
                    className="form-input"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="action-buttons">
          <button
            onClick={saveConfiguration}
            disabled={saving}
            className="btn btn-primary"
          >
            {saving ? 'Saving...' : 'Save Configuration'}
          </button>
          <button
            onClick={resetToDefault}
            className="btn btn-secondary"
          >
            Reset to Default
          </button>
        </div>

        {/* Configuration Preview */}
        <div className="config-section">
          <h3>Configuration Preview</h3>
          <pre className="config-preview">
            {JSON.stringify(config, null, 2)}
          </pre>
        </div>
      </div>
    </div>
  );
};

export default TimetableSettings;
